import React from "react";
import { afField } from "@autodromo/contextual-fields";
import { sanitizeSvg } from "../../../utils/sanitize";
import { formatGtagLabel } from "../../../utils/formatGtagLabel";
import { buttonShowsIcon, getLinkUrl, resolveButtonMinRem, shouldOpenInNewTab } from "../logic";
import { STRINGS } from "../strings";
import { DesktopButtonLink, DesktopButtonText, MobileCta } from "../HeaderV2.styles";
import { ButtonVariant, IconAlignment, type HeaderV2Button } from "../types";

type ButtonRenderProps = {
  button: HeaderV2Button;
  index: number;
  htmlId?: string;
};

const variantOf = ({ button }: { button: HeaderV2Button }): ButtonVariant => button.variant || ButtonVariant.FILLED;

const accentOf = ({ button }: { button: HeaderV2Button }) => button.backgroundColor || undefined;

const iconColorOf = ({ button }: { button: HeaderV2Button }) => {
  const variant = variantOf({ button });
  if (variant === ButtonVariant.OUTLINE) return accentOf({ button }) || "var(--hv2-outline-text)";
  if (variant === ButtonVariant.LINK) return accentOf({ button }) || "var(--hv2-link-text)";
  return button.textColor || "var(--hv2-buttons-text)";
};

const gtagProps = ({ htmlId, label }: { htmlId?: string; label: string }) => ({
  "data-gtag-action": "Clique Botão",
  "data-gtag-label": formatGtagLabel(htmlId, label),
  "data-gtag-category": "Cabeçalho",
});

export const DesktopButton = ({ button, index, htmlId }: ButtonRenderProps) => {
  if (!button.text) return null;
  const label = button.text || STRINGS.buttonFallbackLabel(index);
  const showIcon = buttonShowsIcon({ button });
  const iconRight = button.buttonIconAlignment === IconAlignment.RIGHT;

  return (
    <DesktopButtonLink
      $minRem={resolveButtonMinRem({ withIcon: showIcon })}
      href={getLinkUrl({ item: button })}
      target={shouldOpenInNewTab({ item: button }) ? "_blank" : "_self"}
      aria-label={label}
      title={label}
      data-af-field={afField("buttons", button.id, "text")}
      {...gtagProps({ htmlId, label })}
    >
      <DesktopButtonText
        leftIcon={showIcon && !iconRight ? button.icon : undefined}
        rightIcon={showIcon && iconRight ? button.icon : undefined}
        size="small"
        iconColor={iconColorOf({ button })}
        type="button"
        $variant={variantOf({ button })}
        $accent={accentOf({ button })}
        palette={{
          bgColor: button.backgroundColor || "var(--hv2-buttons-background)",
          borderColor: button.borderColor || "var(--hv2-buttons-border)",
          textColor: button.textColor || "var(--hv2-buttons-text)",
        }}
        className="styleguide"
      >
        <span className="hv2-button-text">{button.text}</span>
      </DesktopButtonText>
    </DesktopButtonLink>
  );
};

export const MobileCtaButton = ({ button, index, htmlId }: ButtonRenderProps) => {
  if (!button.text) return null;
  const label = button.text || STRINGS.buttonFallbackLabel(index);
  const showIcon = buttonShowsIcon({ button });
  const icon = showIcon ? <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeSvg(button.icon!) }} /> : null;
  const iconRight = button.buttonIconAlignment === IconAlignment.RIGHT;

  return (
    <MobileCta
      href={getLinkUrl({ item: button })}
      target={shouldOpenInNewTab({ item: button }) ? "_blank" : "_self"}
      aria-label={label}
      title={label}
      $bg={button.backgroundColor}
      $color={button.textColor}
      $border={button.borderColor}
      $variant={variantOf({ button })}
      $accent={accentOf({ button })}
      data-af-field={afField("buttons", button.id, "text")}
      {...gtagProps({ htmlId, label })}
    >
      {!iconRight && icon}
      <span className="hv2-cta-text">{button.text}</span>
      {iconRight && icon}
    </MobileCta>
  );
};
